import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import { ArrowRight, FileText, ShieldCheck, AlertTriangle, Globe, Landmark, Search } from "lucide-react";
import Footer from "@/components/Footer";
import { trackPageView } from "@/lib/analytics";

const faqSections = [
  {
    id: "triple-lock",
    icon: ShieldCheck,
    title: "The Triple Lock",
    color: "emerald",
    items: [
      {
        q: "What is the Triple Lock?",
        a: (
          <div className="space-y-3">
            <p>
              The Triple Lock concerns the deployment of Irish troops on overseas missions. It sets out that for more than 12 Irish troops to be dispatched on overseas missions there must be:
            </p>
            <ol className="list-decimal list-inside space-y-1 pl-2">
              <li>Cabinet/government approval</li>
              <li>Dáil Éireann approval</li>
              <li>The mission must have a United Nations (UN) mandate from the Security Council or General Assembly.</li>
            </ol>
            <p>
              The Triple Lock is a safeguard that keeps Irish troops out of illegal wars by ensuring that their deployment is in line with the UN Charter. The UN dimension of the Triple Lock has become intolerable for the government and it now plans to remove it.
            </p>
          </div>
        ),
      },
      {
        q: "When did the term Triple Lock come about? How does it relate to the Nice and Lisbon Treaties?",
        a: (
          <div className="space-y-3">
            <p>
              In 2001, the electorate voted No to Nice because of fears that it would undermine neutrality. In 2002, at a European Council meeting in Seville a National Declaration was made stating that: "the participation of ... the Irish Defence Forces in overseas operations, including those carried out under the European security and defence policy, requires:
            </p>
            <ol className="list-[lower-alpha] list-inside space-y-1 pl-2">
              <li>the authorisation of the operation by the Security Council or the General Assembly of the United Nations,</li>
              <li>the agreement of the Irish Government and</li>
              <li>the approval of Dáil Éireann, in accordance with Irish law."</li>
            </ol>
            <p>
              This Declaration, which became known as the Triple Lock, was a repackaging of the 1960 Act. On the basis of this Declaration the electorate voted in favour of Nice II.
            </p>
            <p>
              Following the rejection of Lisbon I in 2008, Ireland's commitment to the Triple Lock was reaffirmed, including in the referendum booklets distributed to each household before the second referendum. On this basis the electorate voted yes to Lisbon II and a protocol was attached to the Treaty asserting that it "does not affect or prejudice Ireland's traditional policy of military neutrality."
            </p>
          </div>
        ),
      },
      {
        q: "Who decides when Irish troops are deployed?",
        a: "All decisions on troop deployment rest solely with the Irish government. The Triple Lock simply commits the government to deploying troops on UN mandated missions only. Moreover, in light of the evolving nature of UN missions, for example the recently approved Gaza stabilisation force, the government should exercise caution when deploying troops overseas, even when a UN mandate has been secured.",
      },
      {
        q: "What will happen if the Triple Lock goes?",
        a: "This or any future government will be able to deploy troops outside of the UN system to war-fighting missions on EU or NATO mandated missions. This would signal the death knell on Irish neutrality and usher in the most significant policy shift since the foundation of the state.",
      },
    ],
  },
  {
    id: "un-veto",
    icon: Globe,
    title: "UN Security Council & Vetoes",
    color: "orange",
    items: [
      {
        q: "How does a UN mandated mission come about?",
        a: "The UN Security Council (UNSC) is responsible for international peace and security. It has 15 members, five of whom are permanent (P5) and hold veto powers. UN missions are authorised by the Security Council, though the UN General Assembly may approve missions under the Uniting for Peace framework. NATO and EU led missions may, at times, have a UN mandate.",
      },
      {
        q: "The government claims that UN P5 members have a veto over Irish troop deployment. Is this true?",
        a: (
          <div className="space-y-3">
            <p>
              <strong className="text-orange-400">No.</strong> This argument is misleading and false. It is designed to have people believe that P5 members specifically authorise or veto the deployment of Irish troops overseas. This is not the case. The UNSC authorises or vetoes missions as a whole. It does not specifically authorise or veto Ireland's participation in a mission.
            </p>
            <p>
              The wording of the Seville Declaration reflects this where it says "the authorisation of the operation". It does not say the authorisation of Irish troops.
            </p>
            <p>
              If a mission were vetoed it either wouldn't take place or if it were operational it would be wound down. In such a situation, no state would have a mandate to deploy troops and to do so outside the UN system would be in contravention of the UN Charter.
            </p>
          </div>
        ),
      },
      {
        q: "Are P5 vetoes of missions common?",
        a: "No. In 1999 China vetoed the continuation of a UN mission in Macedonia in a dispute regarding Taiwan. There are no other examples of a peacekeeping mission being vetoed.",
      },
      {
        q: "The government says the UNSC is in deadlock, no new missions have been approved since 2014 and therefore Ireland must be able to deploy troops without a UN mandate. Is this true?",
        a: (
          <div className="space-y-3">
            <p>
              <strong className="text-orange-400">No.</strong> There are currently 11 active UN peace-keeping missions around the world. Their mandates are renewed annually at the UNSC without issue. Ireland can deploy to all of these missions without impediment.
            </p>
            <p>
              Moreover, of the 284 resolutions brought before the UNSC between 2020 and 2024, 264 passed with no P5 member exercising a veto.
            </p>
            <p>
              UNSC vetoes can be highly contentious and deeply problematic but those issues will not be resolved by Ireland acting outside a UN mandate.
            </p>
          </div>
        ),
      },
      {
        q: "So what will happen if Ireland defies a UN veto and acts outside a UN mandate?",
        a: "If a P5 member vetoes a mission and Irish troops deploy under another mandate, for example as part of an EU or NATO led mission, that would mean Irish troops are deployed on foreign soil in the knowledge that this is against the express wishes of a global superpower and in breach of the UN charter.",
      },
    ],
  },
  {
    id: "sovereignty",
    icon: Landmark,
    title: "Sovereignty & Democracy",
    color: "emerald",
    items: [
      {
        q: "The government claims that it must dismantle the Triple Lock to 'take back our sovereignty'. Is this true?",
        a: "No. There is no relationship between the UN and Irish sovereignty. In our Constitution, sovereignty is vested in the people. When we exercised our sovereign right to vote in the Nice and Lisbon referendums, we did so on the basis that the Triple Lock would be maintained. The Triple Lock, therefore, is not an external imposition on the state, it was created in response to a demand from the people. Thus far there has been no demand from the people — the sovereign — to remove it.",
      },
      {
        q: "Removing the Triple Lock sounds highly undemocratic. Is it?",
        a: "Yes. The people of Ireland approved two EU treaties only once guarantees in the form of the Triple Lock were given. Removing it without consulting the people is an affront to the democratic process.",
      },
      {
        q: "The government says the world has changed and the Triple Lock is no longer fit for purpose. What does this mean?",
        a: (
          <div className="space-y-3">
            <p>
              When the government says the Triple Lock is no longer fit for purpose it is essentially saying that the UN is no longer fit for purpose, though the UN was designed precisely for the purpose of dealing with geopolitical tension through dialogue and diplomacy, and thereby avoiding unsanctioned military deployments and war.
            </p>
            <p>
              At present, the world is experiencing heightened instability and the normalisation of war and genocide. This is precisely because states are undermining the UN system, not because of it. If states continue along this trajectory geopolitical tensions will continue to increase rather than dissipate. It is the responsibility of each state to redouble efforts to uphold international law, not to find work-arounds so they can operate outside it. This is essentially what removing the Triple Lock would amount to.
            </p>
          </div>
        ),
      },
    ],
  },
  {
    id: "neutrality",
    icon: AlertTriangle,
    title: "Neutrality & Military Engagement",
    color: "orange",
    items: [
      {
        q: "The government says the Triple Lock and neutrality are unrelated. Is this true?",
        a: (
          <div className="space-y-3">
            <p>
              <strong className="text-orange-400">No,</strong> neutrality and the Triple Lock are intrinsically linked. Consider for example if a mission to Ukraine or Palestine were vetoed by Russia or the US respectively, and Ireland were to deploy as part of a force operating outside the UN system, that would see Ireland:
            </p>
            <ul className="list-disc list-inside space-y-1 pl-2">
              <li>Participating in a military operation that is expressly opposed by a global superpower and a party to the conflict.</li>
              <li>Becoming embroiled in a mission that is not a traditional peace-keeping mission but a peace-enforcement war-fighting operation that is very unlikely to bring about peace.</li>
              <li>Losing its neutral status under international law by taking sides in a conflict.</li>
            </ul>
          </div>
        ),
      },
      {
        q: "The government uses the term 'military neutrality' and defines it as being 'characterised by non-membership of military alliances or mutual defence arrangements'. What does this mean?",
        a: (
          <div className="space-y-3">
            <p>
              The term 'military neutrality' has no basis in international law. The term 'neutrality' does.
            </p>
            <p>
              Neutrality is "the legal status arising from the abstention of a state from all participation in a war between other states." It is anchored in customary international law and codified in international treaties. There is also a UN resolution on neutrality, which sets out that "national policies of neutrality are aimed at promoting the use of preventive diplomacy, including through the prevention of conflict, mediation, good offices, fact-finding missions, negotiation, the use of special envoys, informal consultations, peace-building and targeted development activities."
            </p>
            <p>
              There is no basis in international law for 'military neutrality' and less so for the government's characterisation of it as non-membership of military alliances such as NATO. Nevertheless, this flawed definition has been deployed by successive governments to permit incremental engagement in military structures while maintaining the facade that the state is neutral.
            </p>
            <p>
              If neutrality were defined as 'non membership of NATO' this would see the vast majority of the world's 193 states being classified as 'militarily neutral'.
            </p>
          </div>
        ),
      },
      {
        q: "The Taoiseach recently claimed that Ireland can be both militarily neutral while also militarily standing with a country at war. Is this possible?",
        a: "No. These are opposing concepts. It is delusional to claim that Ireland can hold both positions at once.",
      },
      {
        q: "Do Irish troops currently participate in EU and NATO-led activities?",
        a: (
          <div className="space-y-3">
            <p>
              Yes. Irish troops regularly participate in EU Battlegroup (EUBG) and NATO-led training exercises though full deployment on missions would be impeded by the Triple Lock unless a UN mandate were secured.
            </p>
            <p>
              EUBGs are predominantly composed of NATO forces and structured for deployment to combat zones. They focus on the interoperability of troops and compatibility of military equipment across EU states. Ireland participates in the Coalition of the Willing, which has openly spoken of plans to deploy troops to Ukraine and recently suggested engaging the Joint Expeditionary Force (JEF).
            </p>
          </div>
        ),
      },
      {
        q: "Is the government's increase in military spending linked to its embrace of EU military structures?",
        a: "Yes. Increased military spending and targeted procurement of military equipment should be understood, not as part of a strategy to defend the state, but rather to ensure Ireland's seamless military integration into NATO-led operations and EU military structures. This is being done without becoming a full member of NATO and thereby avoiding the scrutiny, oversight, accountability and public uproar that would accompany full integration. It is essentially de facto NATO membership by the backdoor and is much more dangerous. Meanwhile the government continues to say Ireland is 'militarily neutral' and has no plans to join NATO.",
      },
    ],
  },
  {
    id: "defence",
    icon: ShieldCheck,
    title: "Defence & Common Concerns",
    color: "emerald",
    items: [
      {
        q: "So how can Ireland defend itself?",
        a: "Regardless of how much Ireland, as a small state that is not a military power, invests in military equipment, it is unlikely to deter a nuclear superpower from invading the country if it deems it necessary to do so for its military objectives. Our best defence is our neutrality. That means not posing a threat to other states and not participating in military alliances. A robust understanding of security would see investment in measures that actually keep people safe — housing, health care, social services and climate crisis policies — not in militarised security, which will only increase the likelihood of Ireland becoming a target while being insufficient to defend the country if it comes under attack.",
      },
      {
        q: "Does the Triple Lock impede Ireland from evacuating Irish citizens?",
        a: "No. Article 3 (1)(f) of the Defence Amendment Act 2006 permits the deployment of troops overseas to undertake 'humanitarian tasks in response to an actual or potential disaster or emergency.'",
      },
      {
        q: "Would the Triple Lock impede Ireland from responding to an attack on undersea infrastructure in Irish waters?",
        a: "No. The Triple Lock concerns overseas deployment and is not engaged when troops deploy in Irish waters.",
      },
      {
        q: "Would the Triple Lock impede Ireland from defending itself if it were attacked?",
        a: "No. The Triple Lock concerns overseas deployment and is unrelated to Ireland's capacity to defend its territorial integrity.",
      },
      {
        q: "So, in conclusion, is Ireland safer with the Triple Lock?",
        a: (
          <div className="space-y-3">
            <p>
              <strong className="text-emerald-400">Yes.</strong> Removing it will put people in harm's way.
            </p>
            <p>
              It is therefore essential that we protect the Triple Lock as a bare minimum, particularly considering that neutrality is not protected in the Constitution. We must reclaim and strengthen Irish neutrality by prohibiting the use of Shannon airport by the US and other militaries, withdrawing from EU military structures and demanding a full British withdrawal from Ireland.
            </p>
          </div>
        ),
      },
    ],
  },
];

export default function FactSheet() {
  const [searchQuery, setSearchQuery] = useState("");
  useEffect(() => { trackPageView("fact-sheet"); }, []);

  const getFilteredSections = () => {
    if (!searchQuery.trim()) return faqSections;
    const q = searchQuery.toLowerCase();
    return faqSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => item.q.toLowerCase().includes(q)),
      }))
      .filter((section) => section.items.length > 0);
  };

  const filteredSections = getFilteredSections();
  const totalResults = filteredSections.reduce((sum, s) => sum + s.items.length, 0);

  return (
    <main data-testid="factsheet-page" className="pt-24 bg-slate-950">
      {/* Hero Section */}
      <section className="py-16 md:py-24" data-testid="factsheet-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2.5 rounded-xl bg-emerald-600/20 border border-emerald-500/30">
                <FileText className="w-5 h-5 text-emerald-400" />
              </div>
              <span className="text-sm font-semibold tracking-widest uppercase text-orange-500">
                Triple Lock Fact Sheet
              </span>
            </div>
            <h1 className="font-serif text-4xl md:text-6xl font-bold text-slate-50 mt-4 mb-6">
              Know the <span className="text-emerald-400">Facts</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl">
              This fact sheet was compiled in response to the many questions that arose during the Neutrality Roadshow — an initiative that travelled around Ireland hosting conversations on the importance of safeguarding the Triple Lock and strengthening Irish neutrality.
            </p>
          </div>
        </div>
      </section>

      {/* Search Bar */}
      <section className="bg-slate-950 py-8" data-testid="factsheet-search">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <Input
              type="text"
              placeholder="Search questions... e.g. 'veto', 'sovereignty', 'NATO'"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 bg-slate-800 border-slate-700 text-slate-100 py-6 rounded-xl text-base"
              data-testid="faq-search-input"
            />
          </div>
          {searchQuery && (
            <p className="text-sm text-slate-400 mt-2" data-testid="faq-search-results">
              {totalResults === 0
                ? "No matching questions found."
                : `Showing ${totalResults} question${totalResults !== 1 ? "s" : ""}`}
            </p>
          )}
        </div>
      </section>

      {/* Context Banner */}
      <section className="bg-slate-900 border-y border-slate-800" data-testid="factsheet-context">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-10">
          <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-6 md:p-8">
            <h2 className="font-serif text-lg md:text-xl font-semibold text-slate-100 mb-3">
              Background
            </h2>
            <p className="text-slate-300 leading-relaxed text-sm md:text-base">
              On 28 February 2026, the US and Israel launched an illegal assault on Iran that ignited a regional war with global ramifications. In response, at least eight European states deployed warships to the eastern Mediterranean. The Irish government failed to condemn the illegal US-Israeli strikes and refused to characterise them as illegal even though the UN Secretary General had recognised them as such. This failure comes at a time when the government is in the process of revoking legislation known as the Triple Lock that has kept Irish troops out of illegal wars. Without it there will be no impediment to where Irish troops may be deployed.
            </p>
          </div>
        </div>
      </section>

      {/* History Context */}
      <section className="py-12 bg-slate-950" data-testid="factsheet-history">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl mx-auto">
            <div className="grid sm:grid-cols-2 gap-6 mb-12">
              <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-6">
                <p className="text-emerald-400 font-bold text-2xl mb-1">1955</p>
                <p className="text-slate-300 text-sm">Ireland joined the UN and began deploying troops on UN peace-keeping missions in 1958.</p>
              </div>
              <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-6">
                <p className="text-emerald-400 font-bold text-2xl mb-1">1960</p>
                <p className="text-slate-300 text-sm">Legislation introduced committing the state to deploying troops on UN mandated missions only.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Sections */}
      {filteredSections.map((section) => {
        const Icon = section.icon;
        const isEmerald = section.color === "emerald";
        return (
          <section
            key={section.id}
            className={`py-12 ${isEmerald ? "bg-slate-950" : "bg-slate-900"}`}
            data-testid={`factsheet-section-${section.id}`}
          >
            <div className="max-w-4xl mx-auto px-6 md:px-12">
              <div className="flex items-center gap-3 mb-8">
                <div
                  className={`p-3 rounded-xl border ${
                    isEmerald
                      ? "bg-emerald-600/20 border-emerald-500/30"
                      : "bg-orange-600/20 border-orange-500/30"
                  }`}
                >
                  <Icon
                    className={`w-5 h-5 ${
                      isEmerald ? "text-emerald-400" : "text-orange-400"
                    }`}
                  />
                </div>
                <h2 className="font-serif text-xl md:text-2xl font-semibold text-slate-100">
                  {section.title}
                </h2>
              </div>

              <Accordion type="single" collapsible className="space-y-3">
                {section.items.map((item, idx) => (
                  <AccordionItem
                    key={idx}
                    value={`${section.id}-${idx}`}
                    className="border border-slate-700/60 rounded-xl overflow-hidden bg-slate-800/30 px-5"
                    data-testid={`faq-item-${section.id}-${idx}`}
                  >
                    <AccordionTrigger
                      className={`text-base font-medium text-slate-100 hover:no-underline py-5 ${
                        isEmerald
                          ? "hover:text-emerald-400"
                          : "hover:text-orange-400"
                      }`}
                      data-testid={`faq-trigger-${section.id}-${idx}`}
                    >
                      {item.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-300 leading-relaxed text-sm md:text-base pb-5">
                      {item.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </section>
        );
      })}

      {/* Source Attribution */}
      <section className="py-10 bg-slate-900 border-t border-slate-800" data-testid="factsheet-source">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <p className="text-sm text-slate-400">
            Source: <span className="text-slate-300">Triple Lock Fact Sheet, April 2026</span> — compiled by the Neutrality Roadshow initiative.
          </p>
          <p className="text-sm text-slate-400 mt-2">
            For further information and updates:{" "}
            <a
              href="https://www.keepirelandneutral.ie"
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 hover:underline"
            >
              www.keepirelandneutral.ie
            </a>
            {" | "}
            <span className="text-orange-400">@neutrality_now</span>
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-slate-950 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/10 via-transparent to-orange-900/10" />
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
          <h2 className="font-serif text-3xl md:text-4xl font-semibold text-slate-100 mb-6">
            Protect the Triple Lock
          </h2>
          <p className="text-lg text-slate-400 mb-10">
            Now that you know the facts, make your voice heard. Email your representatives today.
          </p>
          <Link to="/take-action">
            <Button
              data-testid="factsheet-cta-btn"
              className="bg-emerald-600 hover:bg-emerald-500 text-white px-10 py-6 rounded-full text-lg font-bold transition-all hover:-translate-y-1 shadow-[0_0_30px_-5px_rgba(22,163,74,0.5)]"
            >
              Email Your Representatives
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </main>
  );
}
